import { useEffect, useState } from 'react'
import {
  generateInvoice, generateEmail, computeTotals,
  getOpenRouterKey, setOpenRouterKey, getModel, setModel, DEFAULT_MODEL, listModels, type AiModel,
  type InvoiceDraft, type InvoiceItem,
} from '../lib/ai'

const RECOMMENDED: [string, string][] = [
  ['anthropic/claude-haiku-4.5', 'Claude Haiku 4.5 (rapide, économique)'],
  ['anthropic/claude-sonnet-4.5', 'Claude Sonnet 4.5 (qualité)'],
  ['openai/gpt-4o-mini', 'GPT-4o mini'],
  ['google/gemini-2.5-flash', 'Gemini 2.5 Flash'],
]
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'

const MAIL_API = import.meta.env.VITE_API as string
const money = (n: number) => n.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })

interface Client { id: string; company: string | null; full_name: string | null; email: string | null }

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-anthracite/40 border border-white/10 rounded p-6 relative">
      <div className="absolute left-0 top-0 h-full w-1 bg-orange rounded-l" />
      <h2 className="font-display text-2xl tracking-wide mb-4">{title}</h2>
      {children}
    </section>
  )
}

export default function AITools() {
  const { profile, signOut } = useAuth()
  const [clients, setClients] = useState<Client[]>([])
  const [clientId, setClientId] = useState('')
  const [projects, setProjects] = useState<{ id: string; title: string | null }[]>([])
  const [projectId, setProjectId] = useState('')

  const [key, setKey] = useState(getOpenRouterKey())
  const [model, setModelState] = useState(getModel())
  const [savedCfg, setSavedCfg] = useState(false)
  const [models, setModels] = useState<{ configured: boolean; models: AiModel[] } | null>(null)
  useEffect(() => { listModels().then(setModels).catch(() => setModels({ configured: false, models: [] })) }, [])
  const current = models?.models.find(m => m.id === model)

  const [invPrompt, setInvPrompt] = useState('')
  const [inv, setInv] = useState<InvoiceDraft | null>(null)
  const [invLoading, setInvLoading] = useState(false)
  const [invMsg, setInvMsg] = useState('')

  const [emIntention, setEmIntention] = useState('')
  const [emTo, setEmTo] = useState('')
  const [em, setEm] = useState<{ subject: string; body: string } | null>(null)
  const [emLoading, setEmLoading] = useState(false)
  const [emMsg, setEmMsg] = useState('')

  useEffect(() => {
    supabase.from('clients').select('id,company,full_name,email').order('company').then(({ data }) => {
      if (data) setClients(data as Client[])
    })
  }, [])

  useEffect(() => {
    setProjectId('')
    if (!clientId) { setProjects([]); return }
    supabase.from('projects').select('id,title').eq('client_id', clientId).order('created_at', { ascending: false })
      .then(({ data }) => setProjects((data as any) || []))
  }, [clientId])

  const selectedClient = clients.find(c => c.id === clientId)

  function saveCfg() {
    setOpenRouterKey(key); setModel(model); setSavedCfg(true); setTimeout(() => setSavedCfg(false), 1500)
  }

  async function runInvoice() {
    setInvLoading(true); setInvMsg('')
    try { setInv(await generateInvoice(invPrompt)) }
    catch (e: any) { setInvMsg(e.message) }
    finally { setInvLoading(false) }
  }
  function editItem(i: number, patch: Partial<InvoiceItem>) {
    if (!inv) return
    const items = inv.items.map((it, idx) => idx === i ? { ...it, ...patch } : it)
    setInv({ ...inv, items, ...computeTotals(items) })
  }
  async function saveInvoice() {
    if (!inv) return
    if (!clientId || !projectId) { setInvMsg('Sélectionnez un client et un projet (requis).'); return }
    setInvMsg('Enregistrement…')
    const { data: invoice, error } = await supabase.from('invoices')
      .insert({
        client_id: clientId, project_id: projectId,
        subtotal: inv.subtotal, tps_amount: inv.tps, tvq_amount: inv.tvq,
        tax_amount: inv.tps + inv.tvq, amount: inv.total,
        status: 'brouillon', notes: inv.notes,
      })
      .select().single()
    if (error) { setInvMsg('Erreur : ' + error.message); return }
    const rows = inv.items.map((it, idx) => ({
      invoice_id: invoice.id, description: it.description, quantity: it.quantity,
      unit_price: it.unit_price, amount: it.quantity * it.unit_price, sort_order: idx,
    }))
    const { error: e2 } = await supabase.from('invoice_items').insert(rows)
    setInvMsg(e2 ? 'Facture créée, lignes en erreur : ' + e2.message : '✅ Facture enregistrée (brouillon).')
  }

  async function runEmail() {
    setEmLoading(true); setEmMsg('')
    try {
      setEm(await generateEmail({ intention: emIntention, clientName: selectedClient?.company || undefined }))
      if (selectedClient?.email) setEmTo(selectedClient.email)
    } catch (e: any) { setEm({ subject: 'Erreur', body: e.message }) }
    finally { setEmLoading(false) }
  }
  async function sendEmail() {
    if (!em || !emTo) { setEmMsg('Destinataire requis.'); return }
    setEmMsg('Envoi…')
    const { data } = await supabase.auth.getSession()
    const jwt = data.session?.access_token
    try {
      const r = await fetch(`${MAIL_API}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ to: emTo, subject: em.subject, body: em.body }),
      })
      const j = await r.json()
      setEmMsg(r.ok ? '✅ Courriel envoyé à ' + emTo : 'Erreur : ' + (j.error || r.status))
    } catch (e: any) { setEmMsg('Erreur réseau : ' + e.message) }
  }

  const input = 'w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm focus:border-orange outline-none'
  const btn = 'bg-orange hover:bg-orange-hover transition-colors px-5 py-2.5 rounded-sm text-sm font-bold uppercase tracking-wider disabled:opacity-40'

  return (
    <div className="min-h-screen bg-noir text-blanc px-6 md:px-12 py-10 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl tracking-wide">Générateur IA</h1>
        <div className="flex items-center gap-4 text-xs uppercase tracking-widest text-acier">
          <span>{profile?.full_name || profile?.email}</span>
          <button onClick={signOut} className="hover:text-orange">Déconnexion</button>
          <a href="/" className="hover:text-orange">Accueil</a>
        </div>
      </div>

      <Card title="Config IA">
        <p className="text-xs mb-3">{models === null ? <span className="text-acier">Vérification de la clé serveur…</span> : models.configured ? <span className="text-green-300">✅ Clé OpenRouter configurée sur le serveur · {models.models.length} modèles disponibles</span> : <span className="text-orange">Aucune clé sur le serveur : saisissez une clé personnelle ci-dessous.</span>}</p>
        <label className="block text-xs uppercase tracking-widest text-acier mb-1">Modèle</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {RECOMMENDED.map(([id, label]) => <button key={id} type="button" onClick={() => setModelState(id)} className={`px-2.5 py-1 rounded-sm text-[11px] border transition-colors ${model === id ? 'border-orange text-orange bg-orange/10' : 'border-white/10 text-acier hover:text-blanc'}`}>{label}</button>)}
        </div>
        <input className={input} list="ai-models" value={model} onChange={e => setModelState(e.target.value)} placeholder={DEFAULT_MODEL} />
        <datalist id="ai-models">{(models?.models || []).map(m => <option key={m.id} value={m.id}>{m.name}{m.vision ? ' · vision' : ''} · {m.prompt.toFixed(2)} $/M entrée</option>)}</datalist>
        {current && <p className="text-xs text-acier mt-1">{current.name} · contexte {Math.round(current.context / 1000)}k · {current.vision ? 'lit les images (reçus) ✅' : '⚠️ ne lit pas les images : le scan de reçus ne fonctionnera pas'} · {current.prompt.toFixed(2)} $ / {current.completion.toFixed(2)} $ par million de jetons</p>}
        <label className="block text-xs uppercase tracking-widest text-acier mb-1 mt-4">Clé personnelle (optionnel, remplace celle du serveur)</label>
        <input className={input} type="password" value={key} onChange={e => setKey(e.target.value)} placeholder="sk-or-v1-…" />
        <button className={btn + ' mt-4'} onClick={saveCfg}>{savedCfg ? '✅ Enregistré' : 'Enregistrer'}</button>
      </Card>

      <Card title="Client & projet">
        <select className={input} value={clientId} onChange={e => setClientId(e.target.value)}>
          <option value="">— Sélectionner un client —</option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>{c.company || c.full_name || c.email}</option>
          ))}
        </select>
        {clientId && (
          <select className={input + ' mt-2'} value={projectId} onChange={e => setProjectId(e.target.value)}>
            <option value="">— Sélectionner un projet (requis pour facturer) —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.title || 'Projet sans titre'}</option>
            ))}
          </select>
        )}
      </Card>

      <Card title="Facture — génération IA">
        <textarea className={input + ' min-h-[90px]'} value={invPrompt} onChange={e => setInvPrompt(e.target.value)}
          placeholder="Décris le mandat : ex. « Vidéo corporative 2 min pour usine, 1 jour tournage + drone, montage + color grading »" />
        <button className={btn + ' mt-3'} disabled={invLoading || !invPrompt} onClick={runInvoice}>
          {invLoading ? 'Génération…' : 'Générer la facture'}
        </button>
        {inv && (
          <div className="mt-5 space-y-2">
            {inv.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
                <input className={input + ' col-span-7'} value={it.description} onChange={e => editItem(i, { description: e.target.value })} />
                <input className={input + ' col-span-2 text-center'} type="number" value={it.quantity} onChange={e => editItem(i, { quantity: +e.target.value })} />
                <input className={input + ' col-span-3 text-right'} type="number" value={it.unit_price} onChange={e => editItem(i, { unit_price: +e.target.value })} />
              </div>
            ))}
            <div className="text-sm text-acier space-y-1 pt-3 border-t border-white/10 text-right">
              <div>Sous-total : {money(inv.subtotal)}</div>
              <div>TPS (5 %) : {money(inv.tps)}</div>
              <div>TVQ (9,975 %) : {money(inv.tvq)}</div>
              <div className="text-blanc font-bold text-base">Total : {money(inv.total)}</div>
            </div>
            <button className={btn + ' mt-2'} onClick={saveInvoice}>Enregistrer dans Supabase</button>
          </div>
        )}
        {invMsg && <p className="text-xs mt-3 text-acier">{invMsg}</p>}
      </Card>

      <Card title="Courriel — génération IA">
        <textarea className={input + ' min-h-[70px]'} value={emIntention} onChange={e => setEmIntention(e.target.value)}
          placeholder="Intention : ex. « relancer après l'envoi du devis », « confirmer la date de tournage »" />
        <button className={btn + ' mt-3'} disabled={emLoading || !emIntention} onClick={runEmail}>
          {emLoading ? 'Rédaction…' : 'Générer le courriel'}
        </button>
        {em && (
          <div className="mt-4 space-y-2">
            <input className={input} value={emTo} onChange={e => setEmTo(e.target.value)} placeholder="Destinataire (courriel)" />
            <input className={input + ' font-bold'} value={em.subject} onChange={e => setEm({ ...em, subject: e.target.value })} />
            <textarea className={input + ' min-h-[180px] whitespace-pre-wrap'} value={em.body} onChange={e => setEm({ ...em, body: e.target.value })} />
            <div className="flex gap-2">
              <button className={btn} onClick={sendEmail}>Envoyer</button>
              <button className={btn + ' bg-anthracite hover:bg-anthracite'} onClick={() => navigator.clipboard.writeText(`${em.subject}\n\n${em.body}`)}>Copier</button>
            </div>
          </div>
        )}
        {emMsg && <p className="text-xs mt-3 text-acier">{emMsg}</p>}
      </Card>
    </div>
  )
}
